import { useParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { ProfileCard } from "../components/Profile/ProfileCard";
import { useEffect, useState } from "react";
import { profileApi } from "../api/profileApi";
import { useAuth } from "../contexts/AuthContext";
import { ProfileTabs } from "../components/Profile/ProfileTabs";

const UserProfile = () => {
  const { userId } = useParams();
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCurrentUser, setIsCurrentUser] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const userData = await profileApi.getUserProfile(userId);
        setUser(userData.user || userData);

        try {
          const listingsData = await profileApi.getUserListings(userId);
          setListings(listingsData.listings || listingsData || []);
        } catch (listingsError) {
          console.log("Could not fetch listings:", listingsError);
          setListings([]);
        }

        setIsCurrentUser(currentUser?._id === userId);
      } catch (err) {
        console.error("Profile fetch error:", err);
        setError(err.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId, currentUser]);

  /**
   * Get a display string for a user's location.
   */
  const formatLocationDisplay = (location) => {
    if (!location) return "Location not set";
    if (location.label) return location.label;
    if (location.addressParts) {
      const { suburb, city, country } = location.addressParts;
      const parts = [suburb, city, country].filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
    }
    return "Location not set";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-24 pb-16">
          <div className="container px-4 mx-auto">
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-igbanwo-mint"></div>
              <span className="ml-2 text-muted-foreground">
                Loading profile...
              </span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-24 pb-16">
          <div className="container px-4 mx-auto">
            <div className="text-center py-16 bg-white rounded-xl border border-border/60">
              <div className="text-5xl mb-4">❌</div>
              <h3 className="text-xl font-semibold mb-2">User Not Found</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {error}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const userData = {
    id: userId,
    name: user.firstName + " " + user.lastName,
    avatar: user.image || "",
    location: formatLocationDisplay(user.location),
    memberSince: new Date(user.createdAt).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    }),
    bio: user.bio || "No bio available yet",
    rating: user.rating || 0,
    listingCount: listings.length || 0,
    exchangesCount: user.exchangeCount || 0,
    isCurrentUser: false,
    isFoundingMember: user.isFoundingMember,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16">
        <div className="container px-4 mx-auto">
          <div className="mb-12 animate-fade-in">
            <ProfileCard {...userData} />
          </div>
          <div className="animate-fade-in">
            <ProfileTabs listings={listings} isCurrentUser={isCurrentUser} />
          </div>
        </div>
      </main>

      <footer className="bg-sage-50 py-8 border-t border-border/60">
        <div className="container px-4 mx-auto text-center text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Thoreau Consulting (PTY) Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default UserProfile;